import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import AdminPanel from "./AdminPanel";
import AdminReceiptPage from "./pages/AdminReceiptPage";
import CustomerReceipt from "./components/CustomerReceipt";
import GoogleSignInButton from "./components/GoogleSignInButton";
import BrandLogo from "./components/BrandLogo";
import { UI_PREVIEW_MODE, USE_FIREBASE_AUTH, isAdminEmail } from "./config";
import { findPreviewCustomerByEmail, findPreviewCustomerByToken, loadPreviewCustomers } from "./previewStore";
import { useAuth } from "./useAuth";
import { apiRequest } from "./utils";
import "./App.css";

function Landing() {
  return <Navigate to="/admin" replace />;
}

function CustomerPortalByToken() {
  const { accessToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    if (UI_PREVIEW_MODE) {
      const found = findPreviewCustomerByToken(accessToken);
      if (!found) {
        setError("Application not found. Please open the private link shared with you.");
      } else {
        setCustomer(found);
      }
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await apiRequest(`/api/customer/${accessToken}`);
        setCustomer(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [accessToken]);

  if (loading) {
    return (
      <div className="portal-page">
        <div className="portal-loading">
          <BrandLogo size="xl" />
          <p>Loading your receipt…</p>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="portal-page">
        <div className="portal-error">
          <p>{error || "Application not found."}</p>
        </div>
      </div>
    );
  }

  return <CustomerReceipt customer={customer} />;
}

function CustomerPortalAuth() {
  const { user, loading: authLoading, signInWithGoogle, signOut, getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [customer, setCustomer] = useState(null);
  const [adminPicker, setAdminPicker] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [viewingAsAdmin, setViewingAsAdmin] = useState(false);
  const sessionLoaded = useRef(false);
  const adminSelection = useRef(false);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      sessionLoaded.current = false;
      adminSelection.current = false;
      setCustomer(null);
      setAdminPicker(false);
      setAllCustomers([]);
      setViewingAsAdmin(false);
      return;
    }

    if (adminSelection.current) return;
    if (sessionLoaded.current) return;

    const loadAdminCustomerList = async () => {
      const token = await getToken();
      if (UI_PREVIEW_MODE) return loadPreviewCustomers();
      return apiRequest("/api/admin/customers", { method: "GET" }, token);
    };

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const token = await getToken();

        if (UI_PREVIEW_MODE) {
          const found = findPreviewCustomerByEmail(user.email);
          if (found) {
            setCustomer(found);
            sessionLoaded.current = true;
            return;
          }
          if (isAdminEmail(user.email)) {
            const list = await loadAdminCustomerList();
            setAllCustomers(list);
            setAdminPicker(true);
            sessionLoaded.current = true;
            return;
          }
          setError("No application found for this account. Please contact Square One Estates.");
          setCustomer(null);
          return;
        }

        try {
          const data = await apiRequest("/api/customer/me", { method: "GET" }, token);
          setCustomer(data);
          sessionLoaded.current = true;
        } catch (err) {
          if (isAdminEmail(user.email) && err.message.includes("No application found")) {
            const list = await loadAdminCustomerList();
            setAllCustomers(list);
            setAdminPicker(true);
            sessionLoaded.current = true;
            return;
          }
          throw err;
        }
      } catch (err) {
        setError(err.message);
        setCustomer(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.uid, user?.email, authLoading, getToken]);

  const handleSignIn = async () => {
    setError("");
    sessionLoaded.current = false;
    adminSelection.current = false;
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err.message || "Google sign-in failed");
    }
  };

  const selectCustomer = (record) => {
    adminSelection.current = true;
    setCustomer(record);
    setAdminPicker(false);
    setViewingAsAdmin(true);
    setError("");
  };

  if (authLoading) {
    return (
      <div className="portal-page">
        <div className="portal-loading">
          <BrandLogo size="xl" />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="portal-page">
        <div className="admin-login-card portal-login-card">
          <p className="admin-eyebrow">Customer Access</p>
          <h2>View Your Receipt</h2>
          <p className="portal-login-note">Sign in with the Google account linked to your application Email ID.</p>
          {USE_FIREBASE_AUTH ? (
            <GoogleSignInButton onClick={handleSignIn} label="Sign in with Google" />
          ) : (
            <p className="admin-error">Firebase is not configured. Add REACT_APP_FIREBASE_* keys to .env</p>
          )}
          {error && <p className="admin-error">{error}</p>}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="portal-page">
        <div className="portal-loading">
          <BrandLogo size="xl" />
          <p>Loading your receipt…</p>
        </div>
      </div>
    );
  }

  if (adminPicker) {
    return (
      <div className="portal-page">
        <div className="portal-signout-bar">
          <span>{user.email} · Admin</span>
          <button type="button" className="admin-btn admin-btn--ghost admin-btn--sm" onClick={signOut}>
            Sign out
          </button>
        </div>
        <div className="admin-login-card portal-login-card portal-picker">
          <p className="admin-eyebrow">Admin Access</p>
          <h2>Select Application</h2>
          <p className="portal-login-note">
            No application is linked to your Google account. Choose a customer receipt to view.
          </p>
          {allCustomers.length === 0 ? (
            <p className="admin-error">No applications registered yet. Add one in Admin → New Application.</p>
          ) : (
            <ul className="portal-picker-list">
              {allCustomers.map((record) => (
                <li key={record.customerId}>
                  <button type="button" className="portal-picker-item" onClick={() => selectCustomer(record)}>
                    <strong>{record.name}</strong>
                    <span>{record.userId ? `${record.userId} · ` : ""}{record.email || "No email"}</span>
                    <span>{record.customerId} · Plot {record.plotNumber}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {error && <p className="admin-error">{error}</p>}
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="portal-page">
        <div className="portal-error">
          <p>{error || "Application not found."}</p>
          <button type="button" className="admin-btn admin-btn--ghost" onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="portal-signout-bar">
        <span>
          {user.email}
          {viewingAsAdmin && " · Viewing as admin"}
        </span>
        <div className="portal-signout-bar__actions">
          {viewingAsAdmin && (
            <button
              type="button"
              className="admin-btn admin-btn--ghost admin-btn--sm"
              onClick={() => {
                adminSelection.current = false;
                setCustomer(null);
                setViewingAsAdmin(false);
                setAdminPicker(true);
              }}
            >
              Back to list
            </button>
          )}
          <button type="button" className="admin-btn admin-btn--ghost admin-btn--sm" onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
      <CustomerReceipt customer={customer} />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/admin/receipt/:customerId" element={<AdminReceiptPage />} />
        <Route path="/customer" element={<CustomerPortalAuth />} />
        <Route path="/customer/:accessToken" element={<CustomerPortalByToken />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
